import type { HelpArticle } from "./articles";

/**
 * Matching a typed question to articles — words, synonyms and typos, no AI
 * (help-panel grill, 2026-11-02).
 *
 * A question is cut into words, the filler is dropped, and each remaining word
 * is looked for in every article: in its title (worth most), its `words`, then
 * its answer and steps. A word counts if it is the same, starts the same (four
 * letters or more), or is one slip away (two for long words) — so "reciept"
 * finds "receipt" and "resched" finds "reschedule".
 */

const FILLER = new Set([
  "a",
  "an",
  "the",
  "i",
  "me",
  "my",
  "we",
  "our",
  "you",
  "your",
  "it",
  "its",
  "is",
  "are",
  "do",
  "does",
  "did",
  "how",
  "what",
  "where",
  "when",
  "why",
  "who",
  "which",
  "can",
  "could",
  "to",
  "of",
  "for",
  "in",
  "on",
  "at",
  "by",
  "with",
  "and",
  "or",
  "from",
  "this",
  "that",
  "there",
  "please",
  "want",
  "need",
  "should",
  "get",
  "be",
  "up",
  "about",
  "into",
  "im",
  "not",
  "dont",
  "doesnt",
  "cant",
  "isnt",
  "wont",
]);

/**
 * Shorthand and spellings people type, mapped to words the articles use.
 * One-way: the typed word gains these, it does not lose itself.
 */
const SYNONYMS: Record<string, readonly string[]> = {
  gcal: ["google", "calendar"],
  appt: ["appointment"],
  appts: ["appointment"],
  resched: ["reschedule"],
  pt: ["patient"],
  pts: ["patient"],
  xray: ["x-ray", "radiograph"],
  xrays: ["x-ray", "radiograph"],
  rx: ["prescription"],
  sms: ["whatsapp", "message"],
  text: ["whatsapp", "message"],
  wa: ["whatsapp"],
  holiday: ["leave"],
  holidays: ["leave"],
  vacation: ["leave"],
  dayoff: ["leave"],
  reserve: ["hold"],
  block: ["hold"],
  blocked: ["hold"],
  doctor: ["dentist", "provider"],
  dr: ["dentist", "provider"],
  receipt: ["payment"],
  cash: ["payment"],
  owed: ["balance", "payment"],
  fee: ["price"],
  fees: ["price"],
  cost: ["price"],
  supplies: ["stock"],
  inventory: ["stock"],
  rota: ["shift"],
  roster: ["rota", "shift"],
  user: ["staff"],
  users: ["staff"],
  employee: ["staff"],
  login: ["password", "sign"],
  logo: ["practice"],
  kids: ["family", "children"],
  child: ["family", "children"],
};

const tokenize = (text: string): string[] =>
  text
    .toLocaleLowerCase()
    .replace(/[’']/g, "")
    .split(/[^a-z0-9-]+/)
    .flatMap((word) => [word, ...(word.includes("-") ? word.split("-") : [])])
    .filter((word) => word.length > 1);

/** Damerau–Levenshtein, stopping early once past `limit`. */
function distance(a: string, b: string, limit: number): number {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i++) rows.push([i]);
  for (let j = 1; j <= b.length; j++) (rows[0] as number[])[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let best = Infinity;
    for (let j = 1; j <= b.length; j++) {
      const row = rows[i] as number[];
      const up = rows[i - 1] as number[];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(
        (up[j] as number) + 1,
        (row[j - 1] as number) + 1,
        (up[j - 1] as number) + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, ((rows[i - 2] as number[])[j - 2] as number) + 1);
      }
      row[j] = value;
      best = Math.min(best, value);
    }
    if (best > limit) return limit + 1;
  }
  return (rows[a.length] as number[])[b.length] as number;
}

/** How well one typed word matches one article word: 0 to 1. */
function closeness(typed: string, word: string): number {
  if (typed === word) return 1;
  if (typed.length >= 4 && word.startsWith(typed)) return 0.85;
  if (word.length >= 4 && typed.startsWith(word)) return 0.75;
  if (typed.length < 4) return 0;
  const limit = typed.length >= 8 ? 2 : 1;
  return distance(typed, word, limit) <= limit ? 0.7 : 0;
}

interface Indexed {
  article: HelpArticle;
  fields: { words: string[]; weight: number }[];
}

const indexCache = new WeakMap<readonly HelpArticle[], Indexed[]>();

function index(articles: readonly HelpArticle[]): Indexed[] {
  const cached = indexCache.get(articles);
  if (cached !== undefined) return cached;
  const built = articles.map((article) => ({
    article,
    fields: [
      { words: tokenize(article.title), weight: 3 },
      { words: article.words.flatMap(tokenize), weight: 2 },
      { words: tokenize([article.answer, ...article.steps].join(" ")), weight: 1 },
    ],
  }));
  indexCache.set(articles, built);
  return built;
}

/**
 * What a typed word may also mean. A slip in the shorthand itself still finds
 * it — "reciept" is one swap from `receipt`, so it gains `payment` too.
 */
function synonymsOf(word: string): readonly string[] {
  const exact = SYNONYMS[word];
  if (exact !== undefined) return exact;
  if (word.length < 5) return [];
  for (const [key, also] of Object.entries(SYNONYMS)) {
    if (key.length >= 5 && distance(word, key, 1) <= 1) return also;
  }
  return [];
}

/** The question's words that carry meaning, each with what it may also mean. */
function meaningfulWords(question: string): string[][] {
  return tokenize(question)
    .filter((word) => !FILLER.has(word))
    .map((word) => [word, ...synonymsOf(word)]);
}

export interface HelpMatch {
  article: HelpArticle;
  score: number;
  /**
   * The article's own words the question matched — in its title, answer or
   * steps, lower-cased as `tokenize` leaves them — so the panel can mark them
   * and the reader sees why it was chosen. A match only through the hidden
   * `words` list marks nothing.
   */
  hits: ReadonlySet<string>;
}

/**
 * The best articles for a question, best first. Empty when nothing clears the
 * bar — a question whose only matches are a word or two in some step's text
 * is better answered with "nothing covers that yet" than with a guess.
 */
export function searchHelp(
  articles: readonly HelpArticle[],
  question: string,
  limit = 3,
): HelpMatch[] {
  const asked = meaningfulWords(question);
  if (asked.length === 0) return [];

  const matches: HelpMatch[] = [];
  for (const { article, fields } of index(articles)) {
    let score = 0;
    let found = 0;
    const hits = new Set<string>();
    for (const variants of asked) {
      let best = 0;
      for (const field of fields) {
        // The `words` list is never shown, so nothing in it is marked.
        const shown = field.weight !== 2;
        for (const word of field.words) {
          for (const variant of variants) {
            const near = closeness(variant, word);
            if (near > 0 && shown) hits.add(word);
            const value = near * field.weight;
            if (value > best) best = value;
          }
        }
      }
      if (best > 0) found++;
      score += best;
    }
    // What was asked has to be in the article: every word of a short
    // question, most of a long one. "sms the lab" is a question about the lab,
    // and an article that only knows the sms half is not its answer.
    const share = found / asked.length;
    const enough = asked.length <= 2 ? share === 1 : share >= 0.6;
    if (score >= 1.4 && enough) matches.push({ article, score: score * (0.5 + share / 2), hits });
  }
  matches.sort((a, b) => b.score - a.score);
  // A runner-up far behind the best is noise beside it, not a second answer.
  const bar = (matches[0]?.score ?? 0) / 2;
  return matches.filter((match) => match.score >= bar).slice(0, limit);
}

/**
 * A line of an article cut into pieces, each saying whether it is a word the
 * question matched — what the panel marks. Words are read the way `tokenize`
 * reads them, so "Re-book" is marked for "book" and "chair's" for "chair".
 */
export function markHits(
  text: string,
  hits: ReadonlySet<string>,
): { text: string; hit: boolean }[] {
  if (hits.size === 0) return [{ text, hit: false }];
  return text
    .split(/([A-Za-z0-9]+(?:[’'-][A-Za-z0-9]+)*)/)
    .filter((piece) => piece !== "")
    .map((piece) => {
      const word = piece.toLocaleLowerCase().replace(/[’']/g, "");
      const hit =
        hits.has(word) || (word.includes("-") && word.split("-").some((part) => hits.has(part)));
      return { text: piece, hit };
    });
}

/** Articles suggested on a page before anything is typed. */
export function helpForPage(
  articles: readonly HelpArticle[],
  pathname: string,
  limit = 5,
): HelpArticle[] {
  const onPage = (prefix: string): boolean =>
    prefix === "/" ? pathname === "/" : pathname === prefix || pathname.startsWith(`${prefix}/`);
  return articles.filter((article) => article.pages.some(onPage)).slice(0, limit);
}
